import { isGridEntry, type HistoryEntry } from './types'

/**
 * Reconstruct the document content as it was right AFTER the entry `targetId`, by reverse-applying
 * every newer entry's window onto the current text. History entries store only the changed window
 * ({start, before(removed), after(added)}), and each was recorded relative to the state just before
 * it — so undoing newest→oldest keeps coordinates valid.
 *
 * Each reverse step verifies that the current text actually contains the entry's `after` segment at
 * `start`; if not (the log diverged, e.g. concurrent multi-user edits), we return null and refuse to
 * restore rather than produce corrupted content.
 *
 * `entries` is the room history in chronological order (oldest first), as stored in Y.Array.
 */
export function reconstructAt(entries: HistoryEntry[], currentText: string, targetId: string): string | null {
  const i = entries.findIndex((e) => e.id === targetId)
  if (i < 0) return null
  let text = currentText
  for (let j = entries.length - 1; j > i; j--) {
    const e = entries[j]!
    // A GRID entry has no text window to reverse — it records cell addresses, not offsets. One in
    // the range means this is not a markdown history and there is nothing safe to reconstruct.
    // (A room is one kind for life, so this is a guard, not an expected path: Canvas restores a grid
    // or slides part through its server restore points instead.)
    if (isGridEntry(e) || e.start === undefined || e.before === undefined || e.after === undefined) {
      return null
    }
    const seg = text.slice(e.start, e.start + e.after.length)
    if (seg !== e.after) return null // history diverged — cannot safely reverse
    text = text.slice(0, e.start) + e.before + text.slice(e.start + e.after.length)
  }
  return text
}

/**
 * The ids of the entries {@link reconstructAt} can rebuild from `currentText` — computed in ONE
 * newest→oldest pass rather than one reconstruction per row. Each reverse step is checked exactly as
 * there, so an entry is in the set iff `reconstructAt(entries, currentText, id)` is not null.
 */
export function reconstructableIds(entries: HistoryEntry[], currentText: string): Set<string> {
  const ok = new Set<string>()
  let text = currentText
  for (let j = entries.length - 1; j >= 0; j--) {
    const e = entries[j]!
    ok.add(e.id) // its state is `text`: every newer entry reversed cleanly
    if (isGridEntry(e) || e.start === undefined || e.before === undefined || e.after === undefined) break
    if (text.slice(e.start, e.start + e.after.length) !== e.after) break
    text = text.slice(0, e.start) + e.before + text.slice(e.start + e.after.length)
  }
  return ok
}

/**
 * The name of a "Restore as part" copy: `<part> - restore YYYYMMDD-HH:mm:ss`, the instant of the
 * restored entry in the member's LOCAL time (the server cannot know their zone; on a collision it
 * appends " (2)"). An unparseable instant falls back to the raw string.
 */
export function restoreCopyName(partTitle: string, iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return `${partTitle} - restore ${iso}`
  const p = (n: number): string => String(n).padStart(2, '0')
  const stamp =
    `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
    `-${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  return `${partTitle} - restore ${stamp}`
}
